/**
 * 会话持久化（#89098）
 *
 * ---------------------------------------------------------------------------
 * localStorage 是**唯一真源**，内存里不留副本
 * ---------------------------------------------------------------------------
 *
 * 每次取 session 都重读 localStorage，承担一次同步读的开销，换「绝不读到脏值」。
 *
 * 为什么不能缓存在内存里：凭据存 localStorage，那么「另一个标签页登出了」
 * 或「另一个标签页刚把刷新句柄轮换了」这类事件，只有重读才能发现。任何长期
 * 驻留的内存副本都会变脏，而变脏的后果不是「多一次 401」——我方句柄一旦轮换
 * 旧的立即失效，拿着脏句柄去续期会把会话彻底弄坏（见 session-manager.ts）。
 *
 * 两家 BaaS SDK 都是这个结论：CloudBase 的 LocalCredentials 注释直接写了
 * 「storage 为凭证的唯一可信来源」；supabase 的 GoTrueClient 每次
 * `__loadSession` 都重读 storage。
 */

import type { CloudAuthStorage, CloudSession } from './types.js';

/**
 * 存储 key 前缀。
 *
 * 带上 publishableKey：同一浏览器可能开着同平台的多个应用，共用一个 key 会让
 * 它们互相顶掉登录态。
 */
const STORAGE_KEY_PREFIX = 'workbuddy-cloud.session.';

/** 内存实现，供测试与无 localStorage 的运行时使用。 */
export function createMemoryStorage(): CloudAuthStorage {
    const map = new Map<string, string>();
    return {
        getItem: (k) => map.get(k) ?? null,
        setItem: (k, v) => void map.set(k, v),
        removeItem: (k) => void map.delete(k),
    };
}

/**
 * 解析出可用的 storage。
 *
 * localStorage 在隐私模式、禁用第三方存储、部分 WebView 里访问即抛，所以要
 * 探测而不是假设存在。探测不到就退回内存版 —— 此时刷新页面会掉登录态，
 * 但**功能可用**，比整个 SDK 初始化就抛异常要好。
 */
export function resolveStorage(candidate?: CloudAuthStorage): CloudAuthStorage {
    if (candidate) {
        return candidate;
    }
    try {
        const probe = '__workbuddy_probe__';
        globalThis.localStorage.setItem(probe, '1');
        globalThis.localStorage.removeItem(probe);
        return globalThis.localStorage;
    } catch {
        return createMemoryStorage();
    }
}

/**
 * 会话读写。
 *
 * 只做「读 / 写 / 删」三件事，不含任何过期判定与刷新逻辑 —— 那些在
 * session-manager.ts。分开是因为持久化的正确性（脏数据、序列化）与并发的
 * 正确性（单飞、commit guard）是两类问题，混在一起两边都难看清。
 */
export class SessionStore {
    private readonly key: string;

    constructor(
        private readonly storage: CloudAuthStorage,
        publishableKey: string,
    ) {
        this.key = STORAGE_KEY_PREFIX + publishableKey;
    }

    /**
     * 读当前会话。
     *
     * 任何解析失败都当作「没有会话」并**顺手清掉**那条脏记录：残留的坏 JSON
     * 会让每次读都失败一次，而用户永远不知道该去清哪个 key。
     * 触发场景真实存在 —— 用户手改 localStorage、SDK 升级换了存储格式。
     */
    read(): CloudSession | null {
        let raw: string | null;
        try {
            raw = this.storage.getItem(this.key);
        } catch {
            return null;
        }
        if (!raw) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw) as unknown;
            return isStoredSession(parsed) ? parsed : this.discard();
        } catch {
            return this.discard();
        }
    }

    write(session: CloudSession): void {
        try {
            this.storage.setItem(this.key, JSON.stringify(session));
        } catch {
            // 写失败（配额满 / 隐私模式）不能让登录整体失败：内存里这次调用
            // 返回的 session 仍然可用，只是刷新页面会掉。
        }
    }

    clear(): void {
        try {
            this.storage.removeItem(this.key);
        } catch {
            // 同上：清理失败不该抛给调用方，登出的语义是「尽力清干净」。
        }
    }

    private discard(): null {
        this.clear();
        return null;
    }
}

/**
 * 校验存储内容的形状。
 *
 * 逐字段检查而不是只判 `typeof === 'object'`：格式升级后旧记录会缺字段，
 * 那时候「读到一个缺 refreshToken 的 session」比「读不到」更难查 ——
 * 它会一路走到续期时才炸。
 */
function isStoredSession(value: unknown): value is CloudSession {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const s = value as Record<string, unknown>;
    return (
        typeof s.accessToken === 'string' &&
        s.accessToken !== '' &&
        typeof s.refreshToken === 'string' &&
        typeof s.expiresAt === 'number' &&
        !!s.user &&
        typeof s.user === 'object'
    );
}
